import { useEffect, useRef } from 'react';
import { Badge, Box, ListItemIcon, Stack, Theme, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { AccountBoxRounded } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StatusLevelCountDocument } from '@/graphql/generated/status-level-count.generated';
import { NetProfileAuthStatusLevel } from '@/graphql/generated/types.generated';
import { useNavigation } from '@/providers/navigation-provider';
import { useSession } from '@/providers/session-provider';
import { useTeamPermissions } from '@/providers/team-permissions-provider';
import { hasRouteTeamPermission } from '@/utils/permissions/has-route-team-permission';
import { usersPathFilterAccess } from '@/utils/users-path-filter';
import { getProfilesPath } from '@/utils/profiles/profiles-path';
import RelativeBadge from '../RelativeBadge';
import { ListItemButton } from './menu/Menu';

/**
 * Shortcut to the profiles page that also says how many profiles need reconnecting. The warning is a
 * quiet amber dot, never a red count, and it goes away once every profile is connected again.
 */
export default function MainNavProfilesStatus() {
    const { t } = useTranslation();
    const { mainNavOpen, setMainNavOpen } = useNavigation();
    const { workspace } = useSession();
    const { teamPermissions } = useTeamPermissions();
    const { pathname } = useLocation();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const path = getProfilesPath(workspace);
    const canSee = usersPathFilterAccess(path, workspace) && hasRouteTeamPermission(path, teamPermissions);
    // Same query and variables as the home "Estado dos Perfis" card, so its refetches land here too.
    const { data, refetch } = useQuery(StatusLevelCountDocument, { variables: {}, skip: !canSee });

    // Reconnects happen in the legacy profiles iframe, which Apollo never hears about.
    // ponytail: refresh on navigation only; poll or listen to a socket event if it must be live.
    // The ref keeps it to real navigations: no extra request on mount or when anything else changes.
    const lastPathname = useRef(pathname);
    useEffect(() => {
        if (lastPathname.current === pathname) return;
        lastPathname.current = pathname;
        if (canSee) refetch();
    }, [pathname, canSee, refetch]);

    if (!data) {
        return null;
    }

    const countOf = (level: NetProfileAuthStatusLevel) =>
        data.statusLevelCount.statusLevels.find((each) => each.statusLevel === level)?.count ?? 0;
    const toReconnect = countOf(NetProfileAuthStatusLevel.Error);
    const connected = countOf(NetProfileAuthStatusLevel.Ok) + countOf(NetProfileAuthStatusLevel.Warning);
    // Zero is branched here: Tolgee drops ICU `=0` cases and `pt` plural rules read 0 as "one".
    const connectedText =
        connected === 0
            ? t('navigation.profiles-status.none')
            : t('navigation.profiles-status.connected', { count: connected });
    const reconnectText = t('navigation.profiles-status.reconnect', { count: toReconnect });

    return (
        <Box sx={{ my: 1, ...(!mainNavOpen && { px: 1 }) }}>
            <Tooltip
                title={toReconnect > 0 ? `${connectedText} · ${reconnectText}` : connectedText}
                arrow
                placement="right"
                disableHoverListener={mainNavOpen}
            >
                <ListItemButton
                    dense
                    href={path}
                    onClick={() => isMobile && setMainNavOpen(false)}
                    data-id="menu-profiles-status"
                    sx={
                        mainNavOpen
                            ? { borderRadius: 3, border: 1, borderColor: 'divider', py: 1 }
                            : { justifyContent: 'center' }
                    }
                >
                    <ListItemIcon sx={{ minWidth: mainNavOpen ? 36 : 0 }}>
                        <Badge variant="dot" color="warning" invisible={mainNavOpen || toReconnect === 0}>
                            <AccountBoxRounded />
                        </Badge>
                    </ListItemIcon>
                    {mainNavOpen && (
                        <>
                            <Box flex={1} minWidth={0}>
                                <Typography noWrap variant="body2">
                                    {connectedText}
                                </Typography>
                                {toReconnect > 0 && (
                                    <Stack direction="row" alignItems="center" gap={0.75}>
                                        <RelativeBadge variant="dot" color="warning" />
                                        <Typography noWrap variant="caption" color="text.secondary">
                                            {reconnectText}
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                            <Typography variant="body2" color="primary" fontWeight={500} sx={{ ml: 1 }}>
                                {t('navigation.profiles-status.manage')}
                            </Typography>
                        </>
                    )}
                </ListItemButton>
            </Tooltip>
        </Box>
    );
}
